import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from './cn.js';

export function CopyButton({ value, label = 'Copy', className }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable (http or denied) — the value is visible to copy manually */
        }
      }}
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100',
        className,
      )}
    >
      {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

/** Horizontal tab bar. items: [{ key, label, href?, onClick?, active }] — pass your router's Link as `linkAs`. */
export function Tabs({ items, linkAs: LinkComp, className }) {
  return (
    <nav className={cn('-mb-px flex gap-1 overflow-x-auto border-b border-slate-200', className)} aria-label="Tabs">
      {items.map((t) => {
        const cls = cn(
          'whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
          t.active
            ? 'border-brand-600 text-brand-700'
            : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700',
        );
        return LinkComp && t.href ? (
          <LinkComp key={t.key} to={t.href} className={cls} aria-current={t.active ? 'page' : undefined}>
            {t.label}
          </LinkComp>
        ) : (
          <button
            key={t.key}
            type="button"
            onClick={t.onClick}
            className={cls}
            aria-current={t.active ? 'page' : undefined}
          >
            {t.label}
          </button>
        );
      })}
    </nav>
  );
}

export function PageHeader({ title, description, actions, back }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {back}
        <h1 className="truncate text-xl font-semibold text-slate-900 sm:text-2xl">{title}</h1>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Rupees display from integer paise. */
export function formatPaise(paise, { currency = 'INR' } = {}) {
  if (paise == null) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(paise / 100);
}

export function formatDate(value, opts = { dateStyle: 'medium' }) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', ...opts }).format(new Date(value));
}

export function formatDateTime(value) {
  return formatDate(value, { dateStyle: 'medium', timeStyle: 'short' });
}

export function timeAgo(value) {
  if (!value) return 'never';
  const s = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86_400)} d ago`;
}
